/* ==========================================================================
   MINT BY MEMPHIS CO. — hero3d.js
   Live 3D BRANDED slab that slowly auto-rotates in a vault light.
   - FRONT: your logo + PSA-style MVH label ("MINT BY MEMPHIS CO. · GEM MT 10").
   - BACK:  acquisition pitch ("Sell Us Your Collection · Get An Offer").
   - Both faces are softly self-lit + a viewer-facing light, so the slab stays
     readable through the entire rotation (never goes dark).
   - Degrades to the static render (index.html fallback) on low-power / reduced-motion.
   ========================================================================== */

// three.js r161 (three@0.161.0, MIT) self-hosted as a theme asset; URL injected by
// layout/aurum.liquid. No third-party CDN. Missing URL degrades to the static plate.
const THREE_URL = (typeof window !== "undefined" && window.MBM_THREE_URL) ? window.MBM_THREE_URL : "";
const LOGO_SRC = (typeof window !== "undefined" && window.MBM_HERO_LOGO) ? window.MBM_HERO_LOGO : "assets/img/logo.png";

function shouldEnable() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  // Mobile WebGL is ENABLED (owner request 2026-06-15): the rotating slab shows on
  // phones too (resize() already has portrait sizing branches). Low-end devices are
  // still filtered below by data-saver, low memory, and the WebGL-support check;
  // reduced-motion users get the static vault plate.
  if (navigator.connection && navigator.connection.saveData) return false;
  if (typeof navigator.deviceMemory === "number" && navigator.deviceMemory < 4) return false;
  try {
    const c = document.createElement("canvas");
    if (!(c.getContext("webgl") || c.getContext("experimental-webgl"))) return false;
  } catch (e) { return false; }
  return true;
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/* ---- FRONT: gold logo + PSA-style label with silver MVH chip ---- */
function makeFrontTexture(THREE, logo) {
  const W = 600, H = 840;
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const SANS = "Arial, 'Helvetica Neue', Helvetica, sans-serif";

  const bg = x.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#181612"); bg.addColorStop(1, "#0a0a09");
  x.fillStyle = bg; x.fillRect(0, 0, W, H);

  // ---- label ----
  const lx = 24, ly = 22, lw = W - 48, lh = 150;
  x.fillStyle = "#c01818"; rr(x, lx, ly, lw, lh, 9); x.fill();
  x.fillStyle = "#f7f4ec"; rr(x, lx + 6, ly + 6, lw - 12, lh - 12, 5); x.fill();
  x.textBaseline = "alphabetic";
  const divX = lx + lw - 128;
  x.strokeStyle = "#dad4c4"; x.lineWidth = 1.5; x.beginPath(); x.moveTo(divX, ly + 14); x.lineTo(divX, ly + 92); x.stroke();
  x.fillStyle = "#141414"; x.textAlign = "left";
  x.font = "bold 21px " + SANS; x.fillText("2025 HIGH-END COLLECTORS", lx + 20, ly + 40);
  x.fillText("MINT BY MEMPHIS CO.", lx + 20, ly + 66);
  x.font = "bold 15px " + SANS; x.fillText("BLACK MATTE WITH GOLD METALLIC", lx + 20, ly + 90);
  x.textAlign = "right";
  x.font = "bold 14px " + SANS; x.fillText("#1", lx + lw - 18, ly + 26);
  x.font = "bold 15px " + SANS; x.fillText("GEM MT", lx + lw - 18, ly + 56);
  x.font = "bold 34px " + SANS; x.fillText("10", lx + lw - 18, ly + 90);
  x.strokeStyle = "#dad4c4"; x.lineWidth = 1.5; x.beginPath(); x.moveTo(lx + 12, ly + 104); x.lineTo(lx + lw - 12, ly + 104); x.stroke();
  x.fillStyle = "#111"; let bxx = lx + 20;
  for (let i = 0; i < 38; i++) { const bw = (i % 5 === 0) ? 3 : 1.6; x.fillRect(bxx, ly + 116, bw, 22); bxx += bw + 2.4; }
  x.fillStyle = "#141414"; x.font = "bold 15px " + SANS; x.textAlign = "right"; x.fillText("123456789", lx + lw - 18, ly + 134);
  // silver MVH chip
  const mw = 80, mh = 30, mxc = lx + lw / 2 + 4, mx = mxc - mw / 2, my = ly + 112;
  const sil = x.createLinearGradient(0, my, 0, my + mh);
  sil.addColorStop(0, "#fbfbfc"); sil.addColorStop(0.46, "#c7cad0"); sil.addColorStop(0.56, "#a9adb5"); sil.addColorStop(1, "#e9ebef");
  x.fillStyle = sil; rr(x, mx, my, mw, mh, 4); x.fill();
  x.strokeStyle = "#8b8f98"; x.lineWidth = 1; rr(x, mx, my, mw, mh, 4); x.stroke();
  x.textBaseline = "middle"; x.font = "900 20px " + SANS; x.textAlign = "left";
  const wM = x.measureText("M").width, wV = x.measureText("V").width, wH = x.measureText("H").width;
  let sx = mxc - (wM + wV + wH) / 2;
  x.fillStyle = "#c01818"; x.fillText("M", sx, my + mh / 2 + 1); sx += wM;
  x.fillStyle = "#1f3a8a"; x.fillText("V", sx, my + mh / 2 + 1); sx += wV;
  x.fillStyle = "#1f3a8a"; x.fillText("H", sx, my + mh / 2 + 1);
  x.textBaseline = "alphabetic"; x.textAlign = "left";

  // ---- card area: gold art-deco frame + logo ----
  const top = ly + lh + 26, cX = 42, cW = W - 84, cBot = H - 38, cH = cBot - top;
  x.strokeStyle = "#c2a44e"; x.lineWidth = 3; rr(x, cX, top, cW, cH, 10); x.stroke();
  x.lineWidth = 1; rr(x, cX + 8, top + 8, cW - 16, cH - 16, 7); x.stroke();
  const cb = 18; x.lineWidth = 2.5; x.strokeStyle = "#caa84a";
  [[cX + 16, top + 16, 1, 1], [cX + cW - 16, top + 16, -1, 1], [cX + 16, cBot - 16, 1, -1], [cX + cW - 16, cBot - 16, -1, -1]].forEach(([px, py, sxx, syy]) => {
    x.beginPath(); x.moveTo(px + cb * sxx, py); x.lineTo(px, py); x.lineTo(px, py + cb * syy); x.stroke();
  });
  if (logo) {
    const maxW = cW - 76, maxH = cH - 76, sc = Math.min(maxW / logo.width, maxH / logo.height);
    const dw = logo.width * sc, dh = logo.height * sc;
    x.drawImage(logo, (W - dw) / 2, top + (cH - dh) / 2, dw, dh);
  } else {
    x.fillStyle = "#c2a44e"; x.textAlign = "center";
    x.font = "700 150px 'Bodoni Moda', serif"; x.fillText("M", W / 2, top + cH * 0.5);
    x.font = "500 24px " + SANS; x.fillText("MINT BY MEMPHIS CO.", W / 2, top + cH * 0.72);
    x.textAlign = "left";
  }

  const t = new THREE.CanvasTexture(cv); t.anisotropy = 8;
  if ("colorSpace" in t) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/* ---- BACK: a real slab has a label on the back too. Ours is a "reverse grade"
        BUYING label, above the acquisition pitch. ---- */
function makeBackTexture(THREE, logo) {
  const W = 600, H = 840;
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const SERIF = "'Bodoni Moda', Didot, Georgia, serif";
  const SANS = "'Jost', Arial, sans-serif";
  const HEAVY = "Arial, 'Helvetica Neue', Helvetica, sans-serif";

  const bg = x.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#181612"); bg.addColorStop(1, "#0a0a09");
  x.fillStyle = bg; x.fillRect(0, 0, W, H);

  // ===== back label (mirrors the front format, but it's a BUYING label) =====
  const lx = 24, ly = 22, lw = W - 48, lh = 150;
  x.fillStyle = "#c01818"; rr(x, lx, ly, lw, lh, 9); x.fill();
  x.fillStyle = "#f7f4ec"; rr(x, lx + 6, ly + 6, lw - 12, lh - 12, 5); x.fill();
  x.textBaseline = "alphabetic";
  const divX = lx + lw - 140;
  x.strokeStyle = "#dad4c4"; x.lineWidth = 1.5; x.beginPath(); x.moveTo(divX, ly + 14); x.lineTo(divX, ly + 92); x.stroke();
  x.textAlign = "left";
  x.fillStyle = "#c01818"; x.font = "bold 24px " + HEAVY; x.fillText("NOW BUYING", lx + 20, ly + 42);
  x.fillStyle = "#141414"; x.font = "bold 15px " + HEAVY; x.fillText("ALL GRADES · ALL ERAS", lx + 20, ly + 66);
  x.fillStyle = "#3a3a3a"; x.font = "bold 12.5px " + HEAVY; x.fillText("TOP DOLLAR · FAST · FAIR · CONFIDENTIAL", lx + 20, ly + 88);
  x.textAlign = "right";
  x.fillStyle = "#141414"; x.font = "bold 14px " + HEAVY; x.fillText("INSTANT", lx + lw - 18, ly + 44);
  x.font = "bold 26px " + HEAVY; x.fillText("OFFER", lx + lw - 18, ly + 78);
  x.strokeStyle = "#dad4c4"; x.lineWidth = 1.5; x.beginPath(); x.moveTo(lx + 12, ly + 104); x.lineTo(lx + lw - 12, ly + 104); x.stroke();
  x.fillStyle = "#111"; let bxx = lx + 20;
  for (let i = 0; i < 30; i++) { const bw = (i % 5 === 0) ? 3 : 1.6; x.fillRect(bxx, ly + 116, bw, 22); bxx += bw + 2.4; }
  x.fillStyle = "#141414"; x.font = "bold 14px " + HEAVY; x.textAlign = "right"; x.fillText("MINTBYMEMPHIS.COM/SELL", lx + lw - 18, ly + 134);
  // MVH silver chip
  const mw = 78, mh = 30, mxc = lx + 250, mx = mxc - mw / 2, my = ly + 112;
  const sil = x.createLinearGradient(0, my, 0, my + mh);
  sil.addColorStop(0, "#fbfbfc"); sil.addColorStop(0.46, "#c7cad0"); sil.addColorStop(0.56, "#a9adb5"); sil.addColorStop(1, "#e9ebef");
  x.fillStyle = sil; rr(x, mx, my, mw, mh, 4); x.fill();
  x.strokeStyle = "#8b8f98"; x.lineWidth = 1; rr(x, mx, my, mw, mh, 4); x.stroke();
  x.textBaseline = "middle"; x.font = "900 20px " + HEAVY; x.textAlign = "left";
  const wM = x.measureText("M").width, wV = x.measureText("V").width, wH = x.measureText("H").width;
  let sx = mxc - (wM + wV + wH) / 2;
  x.fillStyle = "#c01818"; x.fillText("M", sx, my + mh / 2 + 1); sx += wM;
  x.fillStyle = "#1f3a8a"; x.fillText("V", sx, my + mh / 2 + 1); sx += wV;
  x.fillStyle = "#1f3a8a"; x.fillText("H", sx, my + mh / 2 + 1);
  x.textBaseline = "alphabetic"; x.textAlign = "left";

  // ===== card-back area: acquisition pitch =====
  if (logo) {
    x.globalAlpha = 0.06;
    const sc = Math.min((W - 170) / logo.width, 190 / logo.height);
    const dw = logo.width * sc, dh = logo.height * sc;
    x.drawImage(logo, (W - dw) / 2, 252, dw, dh);
    x.globalAlpha = 1;
  }
  x.textAlign = "center";
  x.fillStyle = "#D4AF37"; x.font = "500 20px " + SANS; x.fillText("A C Q U I S I T I O N S", W / 2, 288);
  x.fillStyle = "#f2e6c2"; x.font = "700 70px " + SERIF; x.fillText("Sell Us", W / 2, 380);
  x.font = "700 46px " + SERIF; x.fillText("Your Collection", W / 2, 440);
  x.fillStyle = "rgba(242,242,242,0.72)"; x.font = "400 20px " + SANS;
  x.fillText("PSA slabs · Singles · Autos · Sealed wax", W / 2, 502);
  x.fillText("Whole collections & estates", W / 2, 530);
  const pw = 264, ph = 56, pxp = (W - pw) / 2, py = 576;
  const pg = x.createLinearGradient(0, py, 0, py + ph);
  pg.addColorStop(0, "#E7C964"); pg.addColorStop(1, "#B88A2A");
  x.fillStyle = pg; rr(x, pxp, py, pw, ph, 28); x.fill();
  x.fillStyle = "#0b0b0b"; x.font = "700 21px " + SANS; x.textBaseline = "middle";
  x.fillText("GET AN OFFER  →", W / 2, py + ph / 2 + 1); x.textBaseline = "alphabetic";
  x.fillStyle = "rgba(212,175,55,0.7)"; x.font = "500 17px " + SANS;
  x.fillText("MINT BY MEMPHIS CO.", W / 2, H - 168); // raised: leaves slab's lower ~20% empty so page copy can tuck under cleanly

  const t = new THREE.CanvasTexture(cv); t.anisotropy = 8;
  if ("colorSpace" in t) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function roundedRectShape(THREE, w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

async function init() {
  if (!shouldEnable()) return;

  let THREE;
  try { THREE = await import(/* @vite-ignore */ THREE_URL); }
  catch (e) { return; }

  const canvas = document.getElementById("heroCanvas");
  const hero = document.getElementById("hero");
  if (!canvas || !hero) return;

  const logo = await loadImage(LOGO_SRC);
  try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) {}

  try {
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    if ("outputColorSpace" in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 7);

    const group = new THREE.Group();
    scene.add(group);
    let slabScale = 0.72, baseY = 0.5;

    // Slab holder
    const W = 2.5, H = 3.5, R = 0.14, D = 0.12;
    const geo = new THREE.ExtrudeGeometry(roundedRectShape(THREE, W, H, R),
      { depth: D, bevelEnabled: true, bevelThickness: 0.022, bevelSize: 0.022, bevelSegments: 3, steps: 1, curveSegments: 12 });
    geo.center();
    const body = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: 0x101010, metalness: 0.45, roughness: 0.2 }));
    group.add(body);

    // Front + back faces (both softly self-lit so they read at every angle)
    const frontTex = makeFrontTexture(THREE, logo);
    const front = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.92, H * 0.94),
      new THREE.MeshStandardMaterial({ map: frontTex, emissive: 0xffffff, emissiveMap: frontTex, emissiveIntensity: 0.42, roughness: 0.6, metalness: 0.06 }));
    front.position.z = D / 2 + 0.035; group.add(front);

    const backTex = makeBackTexture(THREE, logo);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.92, H * 0.94),
      new THREE.MeshStandardMaterial({ map: backTex, emissive: 0xffffff, emissiveMap: backTex, emissiveIntensity: 0.5, roughness: 0.65, metalness: 0.05 }));
    back.position.z = -(D / 2 + 0.035); back.rotation.y = Math.PI; group.add(back);

    // Glass sheen
    group.add((() => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(W, H),
        new THREE.MeshStandardMaterial({ color: 0xffffff, transparent: true, opacity: 0.05, roughness: 0.04, metalness: 0.9 }));
      m.position.z = D / 2 + 0.05; return m;
    })());

    // Lighting. Viewer-facing fill keeps whatever side faces us lit
    scene.add(new THREE.AmbientLight(0xc2cad8, 0.7));
    const key = new THREE.DirectionalLight(0xfff2dc, 2.0); key.position.set(-3, 4, 5); scene.add(key);
    const cam = new THREE.DirectionalLight(0xffffff, 1.35); cam.position.set(0, 0.5, 6); scene.add(cam);
    const rim = new THREE.PointLight(0xD4AF37, 16, 22); rim.position.set(3.2, -1.5, 2.5); scene.add(rim);

    // Dust motes
    const N = 150, pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) { pos[i*3] = (Math.random()-0.5)*9; pos[i*3+1] = (Math.random()-0.5)*7; pos[i*3+2] = (Math.random()-0.5)*4 - 0.5; }
    const pg = new THREE.BufferGeometry(); pg.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const dust = new THREE.Points(pg, new THREE.PointsMaterial({ color: 0xD4AF37, size: 0.022, transparent: true, opacity: 0.5, sizeAttenuation: true, depthWrite: false }));
    scene.add(dust);

    // Invisible, camera-facing hit-zone so the slab is reliably clickable at ANY spin angle.
    const hitPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(W * 1.05, H * 1.02),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide })
    );
    scene.add(hitPlane);

    function resize() {
      // Canvas fills the hero. On desktop the slab is raised into the upper area as a
      // centered centerpiece, with the headline + CTAs beneath it (baseY raises it).
      const desktop = window.matchMedia("(min-width: 901px)").matches;
      const w = canvas.clientWidth || hero.clientWidth;
      const h = canvas.clientHeight || hero.clientHeight;
      renderer.setSize(w, h, false);
      const a = w / h;
      if (desktop) {
        // Raise the slab above the copy. Smaller + higher on short viewports so its
        // lower edge clears the headline; bigger centerpiece on tall screens.
        // Big slab; the copy tucks under its lower EMPTY edge (texts on both faces
        // sit higher than the page copy, so nothing collides).
        if (h < 820) { slabScale = 0.56; baseY = 0.8; }
        else { slabScale = 0.78; baseY = 0.8; }
      }
      else if (a < 0.75) { slabScale = 0.46; baseY = 0.62; }   // phone portrait: lower the slab (baseY = vertical position, higher = higher on screen)
      else if (a < 1.0) { slabScale = 0.56; baseY = 0.8; }
      else { slabScale = 0.72; baseY = 0.5; }
      group.scale.setScalar(slabScale); hitPlane.scale.setScalar(slabScale);
      camera.aspect = a; camera.position.z = 7; camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener("resize", resize, { passive: true });

    // The slab is a clickable, context-aware CTA: front face → Shop, back face → Sell.
    const ray = new THREE.Raycaster(); const ndc = new THREE.Vector2();
    function hitSlab(e) {
      const rect = canvas.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      ray.setFromCamera(ndc, camera);
      return ray.intersectObject(hitPlane, false).length > 0;
    }
    canvas.addEventListener("pointermove", (e) => { canvas.style.cursor = hitSlab(e) ? "pointer" : ""; }, { passive: true });
    canvas.addEventListener("click", (e) => {
      if (!hitSlab(e)) return;
      const ry = ((group.rotation.y % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const frontFacing = ry < Math.PI / 2 || ry > Math.PI * 1.5;
      if (!frontFacing) {
        // Seller-face tap = a seller CTA; count it like every [data-cta] click
        // (schema v1, same fields as the delegated listener). Never blocks entry.
        try {
          if (window.MBMForms && window.MBMForms.track) {
            window.MBMForms.track("seller_cta_click", { cta: "home-slab", page_path: window.location.pathname, transport_type: "beacon" });
          }
        } catch (err) {}
      }
      if (window.MBMVault) { window.MBMVault.enter(frontFacing ? "shop" : "sell"); return; }
      const target = document.querySelector(frontFacing ? "#collections" : "#sell");
      if (target) target.scrollIntoView({ behavior: "smooth" });
    });

    let visible = true;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver((es) => { visible = es[0].isIntersecting; }, { threshold: 0.01 }).observe(hero);
    }
    document.addEventListener("visibilitychange", () => { visible = !document.hidden; });

    const clock = new THREE.Clock();
    let started = false;
    function frame() {
      requestAnimationFrame(frame);
      if (!visible) return;
      const t = clock.getElapsedTime();
      group.rotation.y = t * 0.55;                              // auto-spin (mouse no longer controls it)
      group.rotation.x = -0.03 + Math.sin(t * 0.4) * 0.03;
      group.position.y = baseY + Math.sin(t * 0.6) * 0.06;      // float
      hitPlane.position.y = group.position.y;
      rim.position.x = 3.4 * Math.cos(t * 0.5);
      rim.position.z = 2.5 * Math.sin(t * 0.5) + 1;
      const dp = dust.geometry.attributes.position;
      for (let i = 0; i < N; i++) { dp.array[i*3+1] += 0.004; if (dp.array[i*3+1] > 3.6) dp.array[i*3+1] = -3.6; }
      dp.needsUpdate = true;
      renderer.render(scene, camera);
      if (!started) { started = true; canvas.classList.add("is-ready"); hero.classList.add("webgl-active"); }
    }
    frame();
  } catch (e) {
    hero.classList.remove("webgl-active");
  }
}

init();
